import { useState, useRef, useCallback, useEffect } from 'react';
import { callRealtimeReadTool, mintToken, openaiSdpExchange, reportUsage, endSession, heartbeat, sanitizeForFeed, friendlyVoiceError, pttContractError, pttReleaseEvents, type UsageRecord, type CompanionMode, type RealtimeResponseCreateEvent } from '../lib/realtimeVoice';
import { ArcturianWire, MAX_ARCTURIAN_READ_CALLS_PER_TURN, prepareArcturianResponse, sealArcturianReadTools, shouldSealArcturianReadTools, verifyArcturianMint, type MintContract, type ResponseKind } from '../lib/arcturianWire';
import { ARCTURIAN_RESOLVER_V3, executeArcturianResolver, resolverErrorOutput } from '../lib/arcturianAction';
import { compactAgentStatusToolResult, parseAgentStatusArguments } from '../lib/arcturianAgentStatus';
import { ArcturianPrefaceInjector, prepareArcturianPreface } from '../lib/arcturianContinuity';
import {
  ArcturianReturnController,
  PrefaceReturnBaselineAdapter,
  arcturianReturnDeviceId,
  latestArcturianActionPointer,
  planArcturianReturnNarration,
  type ArcturianActionPointer,
  type ArcturianReturnBatch,
} from '../lib/arcturianReturn';
import { transitionAmbientContext } from '../lib/ambientVoice';
import { shouldInjectVoiceContext } from '../lib/voiceContextPolicy';
import { shouldInterruptVoiceResponse } from '../lib/voiceBargeIn';
import { acquireAudioInput, activeAudioInput, audioConstraint, friendlyAudioInputError, normalizeAudioInputs, replaceAudioInput, shouldReacquireAudioInput, type AudioInputOption } from '../lib/audioInput';
import { confirmVoiceProposal, confirmedVoiceRelay, isRecognitionArtifact } from '../lib/voiceProposal';
import {
  api,
  arcturianActionApi,
  arcturianReturnApi,
  voiceGate,
  type ArcturianConversationEvent,
} from '../api/client';
import {
  buildSelfReport,
  selfReportFingerprint,
  shouldSelfReport,
  submitSelfReport,
  type RealtimeFailure,
} from '../lib/realtimeSelfReport';

export type VoiceState = 'idle' | 'connecting' | 'live' | 'error' | 'stopped';

// A talkback proposal awaiting the operator's confirm (Step 2): the model called
// propose_to_agent, Cloud's gate said confirm_required (or it's a cross-focus
// target). The UI shows a chip; confirm/reject finishes the model round-trip.
export interface PendingProposal {
  callId: string;
  proposalId: string;
  target: string;   // which agent the message would go to (target_session)
  text: string;     // the proposed command
  danger: string;   // danger_class advisory
  crossFocus: boolean; // target differs from the operator's focused agent
}

// 'fed' = the agent text we sent INTO the narrator as context; 'voice' = what
// the narrator spoke; 'you' = (Step 2) the operator. The 'fed' vs 'voice' pair
// is the verification trail — compare narration against what was actually fed
// + the visible agent chat (Alex 2026-06-23).
// context_kind per Codex' Context-Segment-Contract, extended with `app_event`
// for Alex' AgentOS-navigation awareness (operator switched view/agent/page).
export type ContextKind =
  | 'operator_request' | 'agent_message' | 'background_work'
  | 'focus_boundary' | 'ambient_boundary' | 'ambient_message'
  | 'app_event' | 'summary' | 'agent_voice_response';

export interface TranscriptLine {
  role: 'voice' | 'you' | 'fed';
  text: string;
  // Wall-clock stamp so an exported log is analysable offline (Alex 2026-06-24:
  // Logs fürs Feintuning zugänglich machen).
  ts?: number;
  // Verify-panel attribution (Codex gate: segment/epoch must be visible so
  // mis-attribution is catchable). Present on 'fed' lines.
  kind?: ContextKind;
  agent?: string;
  epoch?: number;
  seg?: number;
}

// Step-1 hard limit: 10-min auto-stop (Codex' risk-bound for the first smoke).
const MAX_SESSION_MS = 10 * 60 * 1000;
// Hybrid narration trigger (frozen team policy 2026-06-24): coalesce briefly,
// then speak; force-speak at the latency cap so long tool runs aren't silent.
const COALESCE_MS = 1500;     // earliest narration after a feed
const MAX_LATENCY_MS = 8000;  // hard fallback since first unspoken feed
// Frist für ein ausbleibendes terminales `response.done` (#898). Großzügig
// gewählt: eine lange Narration darf nicht fälschlich als hängend gelten —
// der Watchdog ist die Notbremse, nicht der Normalweg.
const TERMINAL_TIMEOUT_MS = 45000;
// Abort if the DataChannel never opens (Codex 2026-06-24): else state stays
// 'connecting' forever, the mic keeps running, and the 10-min auto-stop (armed
// in onopen) never starts.
const CONNECT_TIMEOUT_MS = 18000;
const AUDIO_INPUT_KEY = 'cloud-voice-audio-input';

function readPreferredAudioInput(): string {
  try { return localStorage.getItem(AUDIO_INPUT_KEY) || ''; } catch { return ''; }
}

interface RealtimeEvent { type: string; [k: string]: unknown }

interface ArcturianTurnState {
  turnId: string;
  resolverResponseId?: string;
  primaryResponseId?: string;
  resolverDone: boolean;
  resolverCallSeen: boolean;
  outputSent: boolean;
  primaryRequested: boolean;
  readToolCallCount?: number;
  primaryToolResponses?: Map<string, ArcturianPrimaryToolResponseState>;
  /** v3: Der Resolver hat eine Statusfrage erkannt. Vor dem Sprechzug läuft
   *  dann ein erzwungener Nachschlag — gemessen 8/8 statt 1/8 (AiApi). */
  statusLookupTarget?: string;
  statusLookupRequested?: boolean;
  /** #1035: Fehlschlag dieses Zuges, der noch auf seine Absichtszeile wartet. */
  pendingReport?: RealtimeFailure;
  reportIntentRequested?: boolean;
}

interface ArcturianPrimaryToolResponseState {
  pendingCallIds: Set<string>;
  responseDone: boolean;
  continuationStarted: boolean;
}

interface ArcturianSessionContract {
  resolverVersion: string;
  resolver: RealtimeResponseCreateEvent;
  primary: RealtimeResponseCreateEvent;
  affect?: RealtimeResponseCreateEvent;
  /** Fehlt ohne `read_tools` — dann bleibt die Statusfrage unbeantwortbar,
   *  und der Auftragspfad sagt das ehrlich, statt etwas zu erfinden. */
  statusLookup?: RealtimeResponseCreateEvent;
  /** Fehlt, wenn AiApi den Zug nicht mitliefert — dann wird ohne Absicht
   *  gemeldet. Ein Bericht ohne Absicht ist unvollstaendig, ein fehlender
   *  Bericht nutzlos. */
  reportIntent?: RealtimeResponseCreateEvent;
  conversationId: string;
}

/**
 * Browser-side persistent AgentOS voice layer. Mints one scoped ephemeral
 * token (AiApi), opens a WebRTC call to OpenAI Realtime (mic + remote
 * audio + event data-channel), feeds focused-agent live text as context, and
 * reports usage exactly-once per response.done. Narrator modes have no tools;
 * Arcturian may use explicitly minted, bearer-scoped read tools.
 *
 * Continuous-flow (Alex' binding directive 2026-06-24): the session is NOT bound
 * to one agent and does NOT rotate on agent switch. It is a single persistent
 * conversation — switching the focused agent is a `focusShift`, an in-stream
 * boundary marker, never a teardown/re-mint. No context loss, no audible break.
 * Cross-agent fidelity is guarded by hard per-event agent labelling + the 3rd-
 * person AgentOS persona, not by connection isolation. See post #1215.
 */
export function useRealtimeVoice(apiBase: string | null, talkback = false) {
  const [state, setState] = useState<VoiceState>('idle');
  // Realtime starts fail-closed: no microphone track reaches OpenAI until the
  // operator deliberately holds a push-to-talk surface.
  const [inputMuted, setInputMutedState] = useState(true);
  // The live mic MediaStream, exposed so a VU meter can prove capture (Alex 2026-06-26).
  const [micStream, setMicStream] = useState<MediaStream | null>(null);
  const [audioInputs, setAudioInputs] = useState<AudioInputOption[]>([]);
  const [selectedAudioInputId, setSelectedAudioInputId] = useState(readPreferredAudioInput);
  const selectedAudioInputRef = useRef(selectedAudioInputId);
  const [activeAudioInputLabel, setActiveAudioInputLabel] = useState('');
  const [audioInputSwitching, setAudioInputSwitching] = useState(false);
  const audioInputSwitchingRef = useRef(false);
  const [audioInputError, setAudioInputError] = useState('');
  // Semantic mode flag — deliberately separate from the input gesture. Every
  // mode now uses push-to-talk, while only agent-transparent must suppress the
  // normal typed operator feed (it would otherwise relay typed chat as speech).
  const [agentTransparent, setAgentTransparent] = useState(false);
  // Explicit, session-local Arcturian mode: only the structured resolver action
  // `navigate_ui/focus_updates/on|off` may change it. The ref is the single
  // policy truth in the hot feed path; React state exposes that same truth to
  // the dock and global mini-bar.
  const [arcturianInforming, setArcturianInformingState] = useState(false);
  const arcturianInformingRef = useRef(false);
  // Relay dedup: last relayed text + time. An identical relay within the window (the
  // runaway self-relay signature) is suppressed instead of looping.
  const lastRelay = useRef<{ text: string; ts: number }>({ text: '', ts: 0 });
  // Talkback (Step 2): when on, mint `talkback-enabled` (adds the propose_to_agent
  // tool). A ref so the async mint uses the live value.
  const talkbackRef = useRef(talkback);
  talkbackRef.current = talkback;
  const [pendingProposal, setPendingProposal] = useState<PendingProposal | null>(null);
  // Server-resolved realtime_endpoint (from /auth/me) — base for mint/usage.
  // A ref so the async response.done usage handler always uses the live value.
  const apiBaseRef = useRef<string | null>(apiBase);
  apiBaseRef.current = apiBase;
  // Abdruck → Zeitpunkt der letzten Meldung (#1035). Pro Browserleben, nicht
  // pro Sitzung: eine Ursache, die jeden Verbindungsversuch scheitern lässt,
  // würde sonst bei jedem Neustart erneut melden.
  const selfReportSeen = useRef<Map<string, number>>(new Map());
  const [error, setError] = useState('');
  // The agent the narrator is currently focused on (drives feed labels). Changes
  // via focusShift WITHOUT tearing the conversation down.
  const [target, setTarget] = useState<string | null>(null);
  const targetRef = useRef<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  // Session usage metrics (Codex fix 5): a record counts as `acked` only when
  // the server confirms accepted|deduped; failed POSTs are `unsent` (never
  // re-sent in Step 1, kept as a lower-bound gap). `allUsageAcked` = no unsent.
  const [usage, setUsage] = useState<{ acked: number; unsent: number; unsentTokens: number; pending: number }>(
    { acked: 0, unsent: 0, unsentTokens: 0, pending: 0 },
  );
  // Session metrics for the FE-measurement report (Codex 2026-06-24): id +
  // start/end so the operator can read the exact Ist-Werte after a test.
  const [meta, setMeta] = useState<{
    id: string;
    startedAt: number;
    endedAt: number | null;
    suppressedContextItems: number;
  }>(
    { id: '', startedAt: 0, endedAt: null, suppressedContextItems: 0 },
  );

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  // The RTCRtpSender for the mic — mute replaceTrack(null)s it so NO audio reaches
  // OpenAI (a disabled track still sends silence, which Whisper hallucinates into
  // phantom phrases — Alex 2026-06-24: muted but the model acted as if I spoke).
  const audioSenderRef = useRef<RTCRtpSender | null>(null);
  const inputMutedRef = useRef(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const voiceSessionId = useRef('');
  // Stable across the whole continuous-flow companion run (incl. future 60-min
  // rollovers); voiceSessionId is per-transport, companionRunId spans them.
  const companionRunId = useRef('');
  const companionModeRef = useRef<CompanionMode>('agentos-narrator');
  const arcturianContractRef = useRef<ArcturianSessionContract | null>(null);
  const arcturianTurnsRef = useRef(new Map<string, ArcturianTurnState>());
  const arcturianReturnControllerRef = useRef<ArcturianReturnController | null>(null);
  const arcturianReturnTurnsRef = useRef(new Map<string, {
    controller: ArcturianReturnController;
    events: ArcturianConversationEvent[];
  }>());
  const arcturianReturnCatchUpRef = useRef(false);
  const arcturianReturnDeviceIdRef = useRef('');
  const pushContextRef = useRef<((
    kind: ContextKind,
    agent: string,
    payload: string,
    speak?: boolean,
    pairId?: string,
  ) => void) | null>(null);
  // Transparent mode (#1235): the last relay awaiting the agent's reply, so the
  // agent's next turn can be threaded back as agent_voice_response with this
  // pair_id (step 6 — emission pending AiApi envelope confirmation).
  const pendingRelay = useRef<{ pairId: string; target: string } | null>(null);
  // The server-side reservation key (= the `session_id` we sent at mint). Set
  // ONLY after a successful mint; cleanup POSTs /session/end with it to free the
  // parallel-session slot immediately (AiApi PR #70).
  const reservationKey = useRef<string | null>(null);
  const autoStopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 30s liveness heartbeat while live → server frees a phantom slot in ~90s
  // (AiApi PR #71) instead of 60 min. `alive:false` → the slot is gone, stop.
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // Hybrid-trigger state: `dirty` = unspoken feed pending; `responseActive` = a
  // narration is generating (never start a 2nd in parallel — Codex).
  const dirtyRef = useRef(false);
  // Count the bulk feed items intentionally kept out of operator-driven
  // Arcturian. This is exported in session meta so the cost cut is measurable
  // rather than an invisible early return.
  const suppressedContextItemsRef = useRef(0);
  const responseActiveRef = useRef(false);
  // Turn-Korrelation (Issue #898, Invarianten aus #886). `responseActiveRef`
  // allein war ein globales Boolean: ein verspätetes Event einer abgebrochenen
  // Response konnte dem NÄCHSTEN Turn zugeschlagen werden. Der Automat bindet
  // jedes Audio-/Transkript-/Function-Event an seine response_id und sperrt
  // abgebrochene Responses. `requireKind:false`, weil AiApi im Narrator-Modus
  // keine kind-Metadata setzt — die response_id-Bindung gilt trotzdem.
  const wireRef = useRef(new ArcturianWire({ requireKind: false }));
  // Die response_id, die wir gerade als aktiv führen. Nur ihr terminales
  // `response.done` darf die Narration wieder freigeben.
  const activeResponseIdRef = useRef<string | null>(null);
  // Manual PTT response waiting for an already active narration to finish.
  // `response.create` while another response is active is rejected by the
  // provider; the pending user turn therefore gets priority at response.done.
  const pendingPttResponseRef = useRef(false);
  const requestPttResponseRef = useRef<(() => void) | null>(null);
  const pttHeldRef = useRef(false);
  const pttStartedAtRef = useRef(0);
  // Notbremse: bleibt das terminale `response.done` aus, würde die Narration
  // sonst dauerhaft stehen. Nach dieser Frist wird der Slot sichtbar verworfen
  // (#886: „nie ungeprüft starten", aber auch nicht ewig blockieren).
  const terminalWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `scheduleNarration` entsteht weiter unten; der Watchdog braucht es aber
  // schon hier. Ref statt Vorziehen, weil die Definitionsreihenfolge sonst die
  // bestehenden useCallback-Abhängigkeiten umbauen müsste.
  const scheduleNarrationRef = useRef<(() => void) | null>(null);
  const startArcturianInformingNarrationRef = useRef<(() => void) | null>(null);
  const firstUnspokenRef = useRef<number | null>(null);
  const coalesceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const returnCoalesceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const returnDeadlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const returnFirstPendingRef = useRef<number | null>(null);
  const connectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openedRef = useRef(false);   // did the dc ever open this session
  const teardownRef = useRef(false); // WE initiated the close (vs unexpected)
  // Context-Segment-Contract (Codex 2026-06-24): every feed item is tagged with
  // a monotonic focus_epoch + context_segment_id. A focus shift bumps the epoch
  // so late events from the old focus can be told apart and never mis-fed as Y.
  const focusEpochRef = useRef(0);
  const segmentIdRef = useRef(0);
  // Ambient is a temporary narration source, never the operator's actual focus.
  // Keeping it separate is safety-critical: talkback/transparent relay only via
  // targetRef, so a background roam can never redirect an operator command.
  const ambientTargetRef = useRef<string | null>(null);
  // Per Codex 20:03: a record is POSTed exactly once. Failures are kept here as
  // `unsent_or_unknown` (lower-bound, never re-sent). No beacon of attempts.
  const unsentOrUnknown = useRef<UsageRecord[]>([]);

  // Tear down the connection WITHOUT mutating UI state — so an error path can
  // clean up and still show its own state (Codex code-review fix 3: stop() used
  // to overwrite 'error' with 'stopped', hiding the error bar).
  // Notbremse für ein ausbleibendes terminales `response.done`. Ohne sie würde
  // die neue „nur done gibt frei"-Regel die Narration dauerhaft anhalten, wenn
  // der Server das Terminal verschluckt (#886 nennt genau diesen Fall benign:
  // `response_cancel_not_active` ohne folgendes done). Wir starten NICHT
  // ungeprüft neu, sondern verwerfen den Slot sichtbar und geben dann frei.
  const clearTerminalWatchdog = useCallback(() => {
    if (terminalWatchdogRef.current) { clearTimeout(terminalWatchdogRef.current); terminalWatchdogRef.current = null; }
  }, []);

  const armTerminalWatchdog = useCallback(() => {
    clearTerminalWatchdog();
    terminalWatchdogRef.current = setTimeout(() => {
      terminalWatchdogRef.current = null;
      const rid = activeResponseIdRef.current;
      if (rid) {
        const discarded = wireRef.current.discard(rid);
        if (discarded?.turnId) arcturianTurnsRef.current.delete(discarded.turnId);
        if (import.meta.env.DEV) console.warn('[voice] kein terminales response.done — Slot verworfen:', rid);
        setError('Realtime-Antwort blieb ohne Abschluss und wurde verworfen. Bitte erneut sprechen.');
      }
      activeResponseIdRef.current = null;
      responseActiveRef.current = false;
      if (pendingPttResponseRef.current) requestPttResponseRef.current?.();
      else if (dirtyRef.current) scheduleNarrationRef.current?.();
    }, TERMINAL_TIMEOUT_MS);
  }, [clearTerminalWatchdog]);

  const clearTriggerTimers = useCallback(() => {
    if (coalesceTimerRef.current) { clearTimeout(coalesceTimerRef.current); coalesceTimerRef.current = null; }
    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
  }, []);

  const clearReturnTimers = useCallback(() => {
    if (returnCoalesceTimerRef.current) {
      clearTimeout(returnCoalesceTimerRef.current);
      returnCoalesceTimerRef.current = null;
    }
    if (returnDeadlineTimerRef.current) {
      clearTimeout(returnDeadlineTimerRef.current);
      returnDeadlineTimerRef.current = null;
    }
    returnFirstPendingRef.current = null;
  }, []);

  const cleanupConnection = useCallback(() => {
    // Mark THIS close as ours, so the dc.onclose/onerror handlers don't
    // misread a normal user-stop / timeout-cleanup as a connection failure
    // (Codex 2026-06-24: openedRef was reset before close → false error).
    teardownRef.current = true;
    if (autoStopTimer.current) { clearTimeout(autoStopTimer.current); autoStopTimer.current = null; }
    if (heartbeatTimer.current) { clearInterval(heartbeatTimer.current); heartbeatTimer.current = null; }
    if (connectTimerRef.current) { clearTimeout(connectTimerRef.current); connectTimerRef.current = null; }
    clearTriggerTimers();
    clearReturnTimers();
    clearTerminalWatchdog();
    setMeta((m) => ({
      ...m,
      ...(m.startedAt && !m.endedAt ? { endedAt: Date.now() } : {}),
      suppressedContextItems: suppressedContextItemsRef.current,
    }));
    dirtyRef.current = false; responseActiveRef.current = false; firstUnspokenRef.current = null;
    pendingPttResponseRef.current = false;
    pttHeldRef.current = false;
    pttStartedAtRef.current = 0;
    arcturianContractRef.current = null;
    arcturianTurnsRef.current.clear();
    arcturianReturnTurnsRef.current.clear();
    arcturianReturnCatchUpRef.current = false;
    arcturianInformingRef.current = false;
    setArcturianInformingState(false);
    const returnController = arcturianReturnControllerRef.current;
    arcturianReturnControllerRef.current = null;
    returnController?.stop();
    companionModeRef.current = 'agentos-narrator';
    // Frischer Automat pro Session: response_ids einer alten Verbindung dürfen
    // in einer neuen niemals als bekannt gelten.
    wireRef.current = new ArcturianWire({ requireKind: false });
    activeResponseIdRef.current = null;
    try { dcRef.current?.close(); } catch { /* already closed */ }
    try { pcRef.current?.close(); } catch { /* already closed */ }
    micRef.current?.getTracks().forEach((t) => t.stop());
    dcRef.current = null; pcRef.current = null; micRef.current = null; audioSenderRef.current = null;
    inputMutedRef.current = true;
    setInputMutedState(true);
    setMicStream(null);
    setActiveAudioInputLabel('');
    // Release the server-side parallel-session slot immediately (AiApi PR #70)
    // so a device switch works without waiting for the orphan-reap. Best-effort.
    if (reservationKey.current) {
      void endSession(reservationKey.current, apiBaseRef.current || undefined);
      reservationKey.current = null;
    }
  }, [clearTriggerTimers, clearReturnTimers, clearTerminalWatchdog]);
  const stop = useCallback(() => { cleanupConnection(); setState('stopped'); }, [cleanupConnection]);

  // Fire a spoken narration IFF there's unspoken feed AND no response is already
  // generating (Codex: never two in parallel; never speak with nothing new).
  const maybeNarrate = useCallback(() => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') return;
    // Arcturian remains operator-driven by default. When the structured local
    // focus_updates switch is on, use the dedicated primary-audio path below;
    // a naked response.create would violate the strict kind/turn correlation.
    if (companionModeRef.current === 'arcturian') {
      if (arcturianInformingRef.current && dirtyRef.current
          && !responseActiveRef.current && !pttHeldRef.current) {
        startArcturianInformingNarrationRef.current?.();
      }
      return;
    }
    if (!dirtyRef.current || responseActiveRef.current) return;
    clearTriggerTimers();
    dirtyRef.current = false;
    firstUnspokenRef.current = null;
    responseActiveRef.current = true;
    dc.send(JSON.stringify({ type: 'response.create' }));
  }, [clearTriggerTimers]);

  // Schedule a narration: coalesce ~1.5 s (ALWAYS — the minimum coalescing
  // window is guaranteed even at a semantic boundary, Codex 2026-06-24: no
  // immediate bypass), with a hard 8 s latency fallback. Never parallel.
  const scheduleNarration = useCallback(() => {
    if (responseActiveRef.current) return; // response.done will re-trigger
    if (!coalesceTimerRef.current) {
      coalesceTimerRef.current = setTimeout(() => { coalesceTimerRef.current = null; maybeNarrate(); }, COALESCE_MS);
    }
    if (!maxTimerRef.current && firstUnspokenRef.current != null) {
      const left = Math.max(0, MAX_LATENCY_MS - (performance.now() - firstUnspokenRef.current));
      maxTimerRef.current = setTimeout(() => { maxTimerRef.current = null; maybeNarrate(); }, left);
    }
  }, [maybeNarrate]);
  scheduleNarrationRef.current = scheduleNarration;

  const sendArcturianResponse = useCallback((
    template: RealtimeResponseCreateEvent,
    kind: Exclude<ResponseKind, 'plain'>,
    turnId: string,
    sourceResponseId?: string,
  ) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') throw new Error('Realtime-Verbindung ist nicht offen');
    // A previous PTT barge-in pauses and mutes the remote sink immediately.
    // Re-enable it only when the next correlated Arcturian response starts;
    // this prevents the cancelled tail from becoming audible on key release.
    if (audioRef.current) {
      audioRef.current.muted = false;
      void audioRef.current.play().catch(() => { /* the PTT gesture normally authorizes playback */ });
    }
    const event = prepareArcturianResponse(template, kind, turnId, sourceResponseId);
    wireRef.current.expect(kind, turnId, sourceResponseId);
    responseActiveRef.current = true;
    dc.send(JSON.stringify(event));
  }, []);

  /** Wuerde dieser Fehlschlag gemeldet — ohne das Zeitfenster zu verbrauchen?
   *  Nur dann lohnt der zusaetzliche Zug fuer die Absichtszeile. */
  const wouldSelfReport = useCallback((failure: RealtimeFailure) =>
    shouldSelfReport(selfReportFingerprint(failure), selfReportSeen.current, Date.now()), []);

  const reportRealtimeFailure = useCallback((failure: RealtimeFailure, intent?: string) => {
    const now = Date.now();
    const fingerprint = selfReportFingerprint(failure);
    if (!shouldSelfReport(fingerprint, selfReportSeen.current, now)) return;
    selfReportSeen.current.set(fingerprint, now);
    const report = buildSelfReport(failure, { intent, occurredAtIso: new Date(now).toISOString() });
    void submitSelfReport(report, api.issues, 'Arcturian').then((result) => {
      const line = result.outcome === 'created'
        ? `🧾 Selbstdiagnose abgelegt: Issue #${result.id}`
        : result.outcome === 'commented'
          ? `🧾 Selbstdiagnose ergänzt: Issue #${result.id} (schon bekannt)`
          : `🧾 Selbstdiagnose konnte nicht abgelegt werden: ${result.reason}`;
      setTranscript((prev) => [...prev, { role: 'voice', text: line, ts: Date.now() }]);
    });
  }, []);

  const startArcturianPrimary = useCallback((
    turn: ArcturianTurnState,
    sourceResponseId?: string,
  ) => {
    if (responseActiveRef.current || turn.primaryRequested || !turn.outputSent || !turn.resolverDone) return;
    const contract = arcturianContractRef.current;
    if (!contract) return;
    // v3: Hat der Resolver eine Statusfrage erkannt, läuft ZUERST der
    // erzwungene Nachschlag — `tool_choice: required` statt `auto`. Der
    // Unterschied ist nicht theoretisch: erlaubt schlägt in 1 von 8 Läufen
    // wirklich nach, verlangt in 8 von 8 (AiApi, 24 Läufe). Alex' „einen
    // Moment, dann nichts" war genau diese Lücke.
    if (turn.statusLookupTarget && !turn.statusLookupRequested && contract.statusLookup) {
      turn.statusLookupRequested = true;
      try {
        sendArcturianResponse(contract.statusLookup, 'arcturian.status_lookup', turn.turnId, sourceResponseId);
        return;
      } catch {
        // Der Nachschlag ist eine Zutat, nicht die Antwort. Scheitert er schon
        // beim Absenden, wird trotzdem gesprochen — sonst hätten wir das
        // Verstummen nur an eine andere Stelle verschoben.
        turn.statusLookupTarget = undefined;
      }
    }
    // Unbounded lookups silently spend a full-persona Realtime turn on every
    // cycle. Two reads cover a legitimate comparison; the following response
    // must answer from those results with its tool gate closed.
    const primaryTemplate = shouldSealArcturianReadTools(turn.readToolCallCount || 0)
      ? sealArcturianReadTools(contract.primary)
      : contract.primary;
    // Nach einem Fehlschlag: erst die Absichtszeile holen, dann sprechen. Der
    // Zug ist textlos und kostet gemessene ~800 ms — die hier niemanden
    // stoeren, weil der Zug ohnehin schon gescheitert ist.
    if (turn.pendingReport && !turn.reportIntentRequested && contract.reportIntent) {
      turn.reportIntentRequested = true;
      try {
        sendArcturianResponse(contract.reportIntent, 'arcturian.report_intent', turn.turnId, sourceResponseId);
        return;
      } catch {
        // Der Bericht darf nicht am Melden scheitern: dann eben ohne Absicht.
        const failure = turn.pendingReport;
        turn.pendingReport = undefined;
        reportRealtimeFailure(failure);
      }
    }
    turn.primaryRequested = true;
    try {
      // The first spoken response has no source. A read-tool continuation is
      // the same server-minted template, correlated to the response whose
      // function call we just answered — never a naked response.create.
      sendArcturianResponse(
        primaryTemplate,
        'arcturian.primary_audio',
        turn.turnId,
        sourceResponseId,
      );
    } catch (cause) {
      turn.primaryRequested = false;
      setError(cause instanceof Error ? cause.message : 'Arcturian-Sprachantwort konnte nicht starten');
    }
  }, [sendArcturianResponse, reportRealtimeFailure]);

  const continueArcturianPrimaryAfterTools = useCallback((
    turn: ArcturianTurnState,
    sourceResponseId: string,
  ) => {
    const toolState = turn.primaryToolResponses?.get(sourceResponseId);
    if (!toolState || !toolState.responseDone || toolState.pendingCallIds.size
        || toolState.continuationStarted) return;
    toolState.continuationStarted = true;
    turn.primaryRequested = false;
    startArcturianPrimary(turn, sourceResponseId);
  }, [startArcturianPrimary]);

  const startArcturianInformingNarration = useCallback(() => {
    const dc = dcRef.current;
    const pushInformingContext = pushContextRef.current;
    if (!dc || dc.readyState !== 'open' || !pushInformingContext
        || companionModeRef.current !== 'arcturian'
        || !arcturianInformingRef.current || !dirtyRef.current
        || responseActiveRef.current || pttHeldRef.current) return;
    clearTriggerTimers();
    dirtyRef.current = false;
    firstUnspokenRef.current = null;
    pushInformingContext(
      'summary',
      targetRef.current || 'AgentOS',
      '[Ausgabeauftrag] Informiere den Operator auf Deutsch kurz über die seit der letzten Ausgabe neu eingegangene Entwicklung des fokussierten Agenten. Nenne keine Systemmarker und erfinde nichts.',
      false,
    );
    const turnId = `turn_informing_${crypto.randomUUID()}`;
    const turn: ArcturianTurnState = {
      turnId,
      resolverDone: true,
      resolverCallSeen: true,
      outputSent: true,
      primaryRequested: false,
    };
    arcturianTurnsRef.current.set(turnId, turn);
    startArcturianPrimary(turn);
  }, [clearTriggerTimers, startArcturianPrimary]);
  startArcturianInformingNarrationRef.current = startArcturianInformingNarration;

  const setArcturianInforming = useCallback((enabled: boolean) => {
    arcturianInformingRef.current = enabled;
    setArcturianInformingState(enabled);
    if (!enabled) {
      // Turning updates off is immediate: no already-coalesced context may
      // produce one last unsolicited sentence after the operator disabled it.
      clearTriggerTimers();
      dirtyRef.current = false;
      firstUnspokenRef.current = null;
    }
  }, [clearTriggerTimers]);

  const startArcturianReturnNarration = useCallback(() => {
    const dc = dcRef.current;
    const controller = arcturianReturnControllerRef.current;
    if (!dc || dc.readyState !== 'open' || !controller) return;
    // An agent result must never speak into the operator's held PTT recording,
    // even though no model response is active during that interval.
    if (responseActiveRef.current || pttHeldRef.current) return;
    const pending = controller.pending();
    if (!pending.length) {
      clearReturnTimers();
      return;
    }
    const pushReturnContext = pushContextRef.current;
    if (!pushReturnContext) return;
    const plan = planArcturianReturnNarration(pending, arcturianReturnCatchUpRef.current);
    const lastSourceId = plan.sourceEvents[plan.sourceEvents.length - 1]?.event_id;
    for (const event of plan.sourceEvents) {
      const instruction = event.event_id === lastSourceId ? `\n\n[Ausgabeauftrag] ${plan.instruction}` : '';
      pushReturnContext(
        'agent_voice_response',
        event.actor || 'AgentOS',
        `[Agenten-Rückmeldung · ${event.event_type}]\n${event.content}${instruction}`,
        false,
        event.action_id || undefined,
      );
    }
    const turnId = `turn_return_${crypto.randomUUID()}`;
    const turn: ArcturianTurnState = {
      turnId,
      resolverDone: true,
      resolverCallSeen: true,
      outputSent: true,
      primaryRequested: false,
    };
    arcturianTurnsRef.current.set(turnId, turn);
    arcturianReturnTurnsRef.current.set(turnId, {
      controller,
      events: plan.events,
    });
    arcturianReturnCatchUpRef.current = false;
    clearReturnTimers();
    startArcturianPrimary(turn);
  }, [clearReturnTimers, startArcturianPrimary]);

  const scheduleArcturianReturnNarration = useCallback((batch?: ArcturianReturnBatch) => {
    if (batch?.catchUp) arcturianReturnCatchUpRef.current = true;
    const controller = arcturianReturnControllerRef.current;
    if (!controller?.pending().length) return;
    if (returnFirstPendingRef.current == null) returnFirstPendingRef.current = performance.now();
    if (!returnCoalesceTimerRef.current) {
      returnCoalesceTimerRef.current = setTimeout(() => {
        returnCoalesceTimerRef.current = null;
        startArcturianReturnNarration();
      }, COALESCE_MS);
    }
    if (!returnDeadlineTimerRef.current) {
      const left = Math.max(0, MAX_LATENCY_MS - (performance.now() - returnFirstPendingRef.current));
      returnDeadlineTimerRef.current = setTimeout(() => {
        returnDeadlineTimerRef.current = null;
        startArcturianReturnNarration();
      }, left);
    }
  }, [startArcturianReturnNarration]);

  const bindArcturianReturn = useCallback((
    pointer: ArcturianActionPointer,
    baseline: PrefaceReturnBaselineAdapter,
    sessionActionId?: string,
  ) => {
    const current = arcturianReturnControllerRef.current;
    if (current?.pointer.conversationId === pointer.conversationId
        && current.pointer.principalUserId === pointer.principalUserId) {
      if (sessionActionId) current.registerSessionAction(sessionActionId);
      return;
    }
    current?.stop();
    if (!arcturianReturnDeviceIdRef.current) {
      arcturianReturnDeviceIdRef.current = arcturianReturnDeviceId();
    }
    const controller = new ArcturianReturnController({
      listEvents: arcturianReturnApi.listEvents,
      acknowledgePresentation: arcturianReturnApi.acknowledgePresentation,
    }, pointer, {
      deviceId: arcturianReturnDeviceIdRef.current,
      baseline,
      onPending: (batch) => scheduleArcturianReturnNarration(batch),
      onError: () => {
        setError((currentError) => currentError
          || 'Agenten-Rückmeldungen derzeit nicht abrufbar');
      },
    });
    arcturianReturnControllerRef.current = controller;
    if (sessionActionId) controller.registerSessionAction(sessionActionId);
    controller.start();
  }, [scheduleArcturianReturnNarration]);

  const startNextArcturianPrimary = useCallback(() => {
    if (responseActiveRef.current) return;
    for (const turn of arcturianTurnsRef.current.values()) {
      if (turn.resolverDone && turn.outputSent && !turn.primaryRequested) {
        startArcturianPrimary(turn);
        return;
      }
    }
  }, [startArcturianPrimary]);

  const requestPttResponse = useCallback(() => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') return;
    if (responseActiveRef.current) {
      pendingPttResponseRef.current = true;
      return;
    }
    pendingPttResponseRef.current = false;
    if (companionModeRef.current === 'arcturian') {
      const contract = arcturianContractRef.current;
      if (!contract) {
        setError('Arcturian-Vertrag fehlt — Zug nicht gestartet');
        return;
      }
      const turnId = `turn_${crypto.randomUUID()}`;
      arcturianTurnsRef.current.set(turnId, {
        turnId, resolverDone: false, resolverCallSeen: false, outputSent: false, primaryRequested: false,
      });
      try {
        sendArcturianResponse(contract.resolver, 'arcturian.resolver', turnId);
      } catch (cause) {
        arcturianTurnsRef.current.delete(turnId);
        setError(cause instanceof Error ? cause.message : 'Arcturian-Resolver konnte nicht starten');
      }
      return;
    }
    responseActiveRef.current = true;
    dc.send(JSON.stringify({ type: 'response.create' }));
  }, [sendArcturianResponse]);
  requestPttResponseRef.current = requestPttResponse;

  // Finish the propose_to_agent round-trip: the model waits for a
  // function_call_output (same call_id) then continues verbally (AiApi). Without
  // this the model hangs.
  const respondToModel = useCallback((callId: string, confirmed: boolean, editedText?: string) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') return;
    const output = JSON.stringify(confirmed ? { confirmed: true, ...(editedText ? { edited_text: editedText } : {}) } : { confirmed: false });
    dc.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: callId, output } }));
    responseActiveRef.current = true; // the model will now speak its acknowledgement
    dc.send(JSON.stringify({ type: 'response.create' }));
  }, []);

  // Handle a propose_to_agent tool-call (Step 2 talkback). Map the model args to
  // Cloud's gate, branch on `gate`. Cross-focus targets NEVER auto-send — confirm
  // first (AiApi). Companion-control intents must never reach here (prompt-side),
  // but we belt-and-suspenders ignore obvious ones.
  const handleProposal = useCallback(async (rawArgs: string, callId: string) => {
    let parsed: Record<string, unknown>;
    try { parsed = JSON.parse(rawArgs); } catch { respondToModel(callId, false); return; }
    const text = String(parsed.text || '').trim();
    const targetSession = String(parsed.session_id || targetRef.current || '').trim();
    const danger = String(parsed.danger_class || 'other');
    if (!text || !targetSession) { respondToModel(callId, false); return; }
    // Kein Agent heißt „0" oder „42". Das Modell füllt `session_id` gelegentlich
    // mit einer Zahl statt eines Namens — Alex bekam daraufhin die Frage
    // „An 0 senden?" gestellt (14.08., Bildschirmfoto). Eine Bestätigungsfrage
    // zu einem Ziel, das es nicht geben kann, ist keine Sicherheit, sondern
    // Verwirrung: Ablehnen, damit das Modell mit einem NAMEN wiederkommt.
    if (/^\d+$/.test(targetSession)) { respondToModel(callId, false); return; }
    // Belt-and-suspenders: never relay a companion-control phrase to an agent.
    if (/^(stopp?|leiser|lauter|ambient (aus|an)|wiederhol|pause|weiter)\b/i.test(text)) { respondToModel(callId, false); return; }
    // Erkennungs-Artefakt (AiApis REGEL 4, hier VOR dem Tor). Nötig, weil
    // Clouds `classify()` ein reiner Gefahren-Ausdruck ist: „Vielen Dank fürs
    // Zuschauen" enthält kein Gefahrenwort, kommt also als `auto_ok` zurück —
    // und bei `auto_ok` sendet dieser Client SOFORT, ohne Bestätigungs-Chip.
    // Der Chip erscheint nur bei `confirm_required` oder außerhalb des Fokus.
    if (isRecognitionArtifact(text)) { respondToModel(callId, false); return; }
    const focus = targetRef.current || '';
    const crossFocus = targetSession !== focus;
    setTranscript((prev) => [...prev, { role: 'fed', text: `[Talkback → ${targetSession}${crossFocus ? ' · cross-focus' : ''}] ${text}`, kind: 'operator_request', agent: targetSession, ts: Date.now() }]);
    // Cross-focus → ALWAYS confirm before submitting (don't let Cloud auto-relay).
    if (crossFocus) {
      setPendingProposal({ callId, proposalId: '', target: targetSession, text, danger, crossFocus: true });
      return;
    }
    const vs = voiceSessionId.current;
    const epoch = focusEpochRef.current;
    await voiceGate.registerFocus(vs, epoch, targetSession);
    const res = await voiceGate.submitProposal({ voiceSessionId: vs, focusEpoch: epoch, targetSession, proposedText: text, targetContext: 'agent' });
    if (res.gate === 'auto_ok') {
      const delivery = confirmedVoiceRelay(res);
      respondToModel(callId, delivery.confirmed);
      setTranscript((prev) => [...prev, {
        role: 'voice',
        text: delivery.confirmed
          ? `✓ an ${targetSession} gesendet`
          : `✗ nicht gesendet (${targetSession}): ${delivery.reason || 'Relay nicht bestätigt'}`,
        ts: Date.now(),
      }]);
    } else if (res.gate === 'denied') {
      respondToModel(callId, false);
      setTranscript((prev) => [...prev, { role: 'voice', text: `✗ abgelehnt: ${res.reason || 'denied'}`, ts: Date.now() }]);
    } else {
      setPendingProposal({ callId, proposalId: res.proposal_id || '', target: targetSession, text: res.send_preview || text, danger, crossFocus: false });
    }
  }, [respondToModel]);

  // Close a relay_to_agent round-trip (transparent mode #1235). Output carries the
  // gate result + pair_id (AiApi contract) — NOT the {confirmed} shape of propose_
  // to_agent. Without this + response.create the model hangs.
  const respondToRelay = useCallback((callId: string, gate: 'auto_ok' | 'denied', pairId: string, reason?: string) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') return;
    const output = JSON.stringify({ gate, pair_id: pairId, ...(reason ? { reason } : {}) });
    dc.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: callId, output } }));
    responseActiveRef.current = true; // model speaks its acknowledgement / the denial
    dc.send(JSON.stringify({ type: 'response.create' }));
  }, []);

  // Handle a relay_to_agent tool-call (transparent mode). Direct fire-and-acknowledge
  // relay — NO confirm-chip (that's propose_to_agent's pattern). Cloud's gate still
  // runs (focus-stale + RBAC, no cost-cap — that's upstream); denied is surfaced,
  // never silent-dropped. pair_id threads the agent's reply back (step 6).
  const handleRelay = useCallback(async (rawArgs: string, callId: string) => {
    let parsed: Record<string, unknown>;
    try { parsed = JSON.parse(rawArgs); } catch { respondToRelay(callId, 'denied', '', 'bad_args'); return; }
    const text = String(parsed.text || '').trim();
    // Transparent mode always relays to the FOCUSED agent — never trust a model-picked
    // session_id. In the feedback-loop incident (vs_GuideDevBot2_95876) it hallucinated
    // "opus" as the target. The operator is talking WITH the focused agent, full stop.
    const targetSession = (targetRef.current || String(parsed.session_id || '')).trim();
    const pairId = String(parsed.pair_id || `pair_${Math.round(performance.now())}`).trim();
    if (!text || !targetSession) { respondToRelay(callId, 'denied', pairId, 'empty_relay'); return; }
    // Belt-and-suspenders: never relay a companion-control phrase to an agent.
    if (/^(stopp?|leiser|lauter|ambient (aus|an)|wiederhol|pause|weiter)\b/i.test(text)) { respondToRelay(callId, 'denied', pairId, 'companion_control'); return; }
    // Dedup: an identical relay within 12s is the runaway self-relay signature (the
    // model re-relaying its own narrated output through an open mic). Suppress it
    // instead of looping (Alex 2026-06-25 — mic-active mode without push-to-talk).
    const norm = text.replace(/\s+/g, ' ').trim().toLowerCase();
    const now = performance.now();
    if (norm === lastRelay.current.text && now - lastRelay.current.ts < 12000) { respondToRelay(callId, 'denied', pairId, 'duplicate_suppressed'); return; }
    lastRelay.current = { text: norm, ts: now };
    setTranscript((prev) => [...prev, { role: 'fed', text: `[Relay → ${targetSession}] ${text}`, kind: 'operator_request', agent: targetSession, ts: Date.now() }]);
    const vs = voiceSessionId.current;
    const epoch = focusEpochRef.current;
    // Same focus registration as proposals — the gate's focus-stale guard keys on it.
    await voiceGate.registerFocus(vs, epoch, targetSession);
    const res = await voiceGate.relayToAgent({ voiceSessionId: vs, focusEpoch: epoch, targetSession, text, pairId });
    const gate: 'auto_ok' | 'denied' = res.gate === 'auto_ok' ? 'auto_ok' : 'denied';
    respondToRelay(callId, gate, pairId, gate === 'denied' ? (res.reason || 'denied') : undefined);
    if (gate === 'auto_ok') {
      pendingRelay.current = { pairId, target: targetSession };
      setTranscript((prev) => [...prev, { role: 'voice', text: `✓ an ${targetSession} weitergeleitet`, ts: Date.now() }]);
    } else {
      setTranscript((prev) => [...prev, { role: 'voice', text: `✗ Relay abgelehnt: ${res.reason || 'denied'}`, ts: Date.now() }]);
    }
  }, [respondToRelay]);

  const sendFunctionOutput = useCallback((callId: string, output: string): boolean => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open' || !callId || !output) return false;
    dc.send(JSON.stringify({
      type: 'conversation.item.create',
      item: { type: 'function_call_output', call_id: callId, output },
    }));
    return true;
  }, []);

  /**
   * Meldet einen Fehlschlag als Issue (#1035) — und sagt es im Verlauf.
   *
   * Alex' Anspruch dahinter, wörtlich: „Ich bin ja kein Kunde bei A1, der nichts
   * wissen darf." Ein stiller Bericht wäre also nur die halbe Sache; er soll
   * sehen, DASS gemeldet wurde und unter welcher Nummer.
   *
   * Absichtlich `void`: Der Meldeweg hängt sich nie in den Fehlerpfad, den er
   * beschreibt. `submitSelfReport` wirft nicht, das Ergebnis wird nur angezeigt.
   */
  const handleArcturianResolver = useCallback(async (
    responseId: string,
    turnId: string,
    rawArgs: string,
    callId: string,
  ) => {
    const contract = arcturianContractRef.current;
    const turn = arcturianTurnsRef.current.get(turnId);
    if (!contract || !turn) return;
    let output: string;
    try {
      output = await executeArcturianResolver({
        callId,
        rawArguments: rawArgs,
        resolverContract: contract.resolverVersion,
        conversationId: contract.conversationId,
        navigate: (name) => window.dispatchEvent(new CustomEvent('cloud-open-agent', { detail: { name } })),
        setFocusUpdates: setArcturianInforming,
        ...(contract.statusLookup
          ? { onStatusQuery: (name: string) => { turn.statusLookupTarget = name; } }
          : {}),
      });
    } catch (cause) {
      output = resolverErrorOutput(cause);
      const code = (() => { try { return JSON.parse(output).error as string; } catch { return 'action_rejected'; } })();
      setTranscript((prev) => [...prev, {
        role: 'voice', text: `✗ Arcturian-Auftrag nicht ausgeführt: ${code}`, ts: Date.now(),
      }]);
      // Der Fehlschlag endete bisher hier — als eine Zeile im Verlauf, die
      // niemand wiederfindet. Genau die Sitzung, in der Alex zweimal
      // hintereinander dasselbe abgewiesen bekam, hätte einen Eintrag verdient.
      const failure: RealtimeFailure = {
        code,
        mode: 'arcturian',
        contract: contract.resolverVersion,
        rule: (() => { try { return JSON.parse(output).detail as string; } catch { return undefined; } })(),
        voiceSessionId: voiceSessionId.current || undefined,
        build: __BUILD_ID__,
        endpoint: apiBaseRef.current || undefined,
      };
      // Der Fehlercode sagt, WAS brach — nie, was der Bediener wollte. Diese
      // eine Zeile kennt nur das Modell, und sie ist der Unterschied zwischen
      // einem Eintrag, den man abarbeiten kann, und einem, der nur belegt.
      //
      // Am 11.08. haben AiApi und ich fuenf plausible Erklaerungen fuer eine
      // Rueckfrage gebaut und alle fuenf selbst widerlegt — 40 gemessene Laeufe.
      // Am Ende fehlte genau ein Satz: was Alex gesagt hat. Deshalb dieser Zug.
      //
      // Zurueckgehalten wird nur, wenn ueberhaupt gemeldet wuerde: fuer einen
      // gebremsten Abdruck einen Zug auszugeben, waere Aufwand ohne Ertrag.
      if (contract.reportIntent && wouldSelfReport(failure)) turn.pendingReport = failure;
      else reportRealtimeFailure(failure);
    }
    // The validated function output is exactly the persisted receipt. Bind the
    // durable return lane even if this Realtime response was cancelled later:
    // an already dispatched action is not rolled back by barge-in.
    try {
      const receipt = JSON.parse(output) as Record<string, unknown>;
      if (receipt.protocol === 'agentos.arcturian-action.v1'
          && typeof receipt.action_id === 'string' && receipt.action_id
          && typeof receipt.conversation_id === 'string' && receipt.conversation_id
          && typeof receipt.principal_user_id === 'string' && receipt.principal_user_id) {
        bindArcturianReturn({
          actionId: receipt.action_id,
          conversationId: receipt.conversation_id,
          principalUserId: receipt.principal_user_id,
          updatedAt: Date.now() / 1000,
        }, new PrefaceReturnBaselineAdapter(null), receipt.action_id);
      }
    } catch {
      // `{}` for none/clarify and typed error outputs intentionally do not bind.
    }
    // A persisted action is never rolled back by barge-in. Its late receipt is
    // nevertheless not injected into an abandoned Realtime response, and no
    // spoken follow-up is started for that cancelled turn.
    const registration = wireRef.current.get(responseId);
    if (!registration || registration.cancelling || registration.turnId !== turnId) return;
    if (!sendFunctionOutput(callId, output)) return;
    turn.outputSent = true;
    startNextArcturianPrimary();
  }, [sendFunctionOutput, startNextArcturianPrimary, bindArcturianReturn, setArcturianInforming,
    reportRealtimeFailure, wouldSelfReport]);

  const handleArcturianReadTool = useCallback(async (
    responseId: string,
    turnId: string,
    name: string,
    rawArgs: string,
    callId: string,
    readAllowed: boolean,
  ) => {
    const turn = arcturianTurnsRef.current.get(turnId);
    const toolState = turn?.primaryToolResponses?.get(responseId);
    if (!turn || !toolState) return;

    let result: unknown;
    if (!readAllowed) {
      result = {
        call_id: callId,
        tool: name,
        ok: false,
        error: 'read_tool_limit_reached',
        retryable: false,
      };
    } else if (name !== 'agent_status') {
      // Mint verification permits future read tools so a harmless AiApi
      // extension does not kill session startup. Until the browser implements
      // one, answer it explicitly instead of leaving the model waiting forever.
      result = {
        call_id: callId,
        tool: name,
        ok: false,
        error: 'unsupported_read_tool',
        retryable: false,
      };
    } else {
      const args = parseAgentStatusArguments(rawArgs);
      result = args
        ? compactAgentStatusToolResult(await callRealtimeReadTool({
          tool: name,
          arguments: args,
          callId,
          sessionId: voiceSessionId.current,
          apiBase: apiBaseRef.current || undefined,
        }))
        : {
          call_id: callId,
          tool: name,
          ok: false,
          error: 'invalid_tool_arguments',
          retryable: false,
        };
    }

    // Tool I/O may finish after response.done (normal) or after teardown /
    // cancellation (must be ignored). Re-read all correlation state rather
    // than trusting the turn object captured before the network request.
    const liveTurn = arcturianTurnsRef.current.get(turnId);
    const registration = wireRef.current.get(responseId);
    if (liveTurn !== turn || !registration || registration.cancelling
        || registration.turnId !== turnId) return;
    if (!sendFunctionOutput(callId, JSON.stringify(result))) {
      toolState.pendingCallIds.delete(callId);
      if (!teardownRef.current) {
        setError('Arcturian-Status konnte nicht an die Sprachsitzung zurückgegeben werden');
      }
      return;
    }
    toolState.pendingCallIds.delete(callId);
    continueArcturianPrimaryAfterTools(turn, responseId);
  }, [continueArcturianPrimaryAfterTools, sendFunctionOutput]);

  const routeFunctionCall = useCallback((
    responseId: string,
    callId: string,
    name: string,
    rawArgs: string,
  ) => {
    const verdict = wireRef.current.functionCall(responseId, callId);
    if (!verdict.accepted) {
      if (import.meta.env.DEV) console.warn('[voice] function-call verworfen:', verdict.reason, responseId, callId);
      return;
    }
    const registration = verdict.registration;
    if (registration.kind === 'arcturian.resolver') {
      if (!registration.turnId) return;
      const turn = arcturianTurnsRef.current.get(registration.turnId);
      if (turn) turn.resolverCallSeen = true;
      if (name !== 'resolve_arcturian_turn') {
        if (sendFunctionOutput(callId, JSON.stringify({ error: 'unexpected_resolver_tool', retryable: false }))) {
          if (turn) { turn.outputSent = true; startNextArcturianPrimary(); }
        }
        return;
      }
      void handleArcturianResolver(responseId, registration.turnId, rawArgs, callId);
      return;
    }
    if (registration.kind === 'arcturian.report_intent') {
      // Der Zug traegt genau ein Werkzeug und genau ein Feld. Alles andere
      // waere eine Einladung, den Fehlschlag zu erklaeren statt ihn zu
      // benennen — und das Modell ist hier per Definition der unzuverlaessige
      // Zeuge: Seine Ausgabe wurde gerade verworfen.
      if (!registration.turnId) return;
      const turn = arcturianTurnsRef.current.get(registration.turnId);
      sendFunctionOutput(callId, '{}');
      const failure = turn?.pendingReport;
      if (turn) turn.pendingReport = undefined;
      if (!failure) return;
      let intent: string | undefined;
      if (name === 'report_intent') {
        try {
          const parsed = JSON.parse(rawArgs) as { intent?: unknown };
          if (typeof parsed.intent === 'string' && parsed.intent.trim()) intent = parsed.intent.trim();
        } catch { /* Ein unlesbarer Satz ist kein Grund, den Bericht zu verlieren. */ }
      }
      reportRealtimeFailure(failure, intent);
      return;
    }
    if (registration.kind === 'agentos.affect') {
      // Affect is deliberately isolated from the action executor. The current
      // web avatar has no affect projection yet, so acknowledge a valid report
      // without allowing it to trigger an action or another spoken response.
      if (name === 'report_affect') sendFunctionOutput(callId, '{}');
      return;
    }
    // Der Nachschlag-Zug ruft dasselbe Lesewerkzeug wie der Sprechzug und
    // zählt gegen dasselbe Budget — sonst wäre er ein zweiter, unbegrenzter
    // Weg zu denselben Aufrufen.
    if (registration.kind === 'arcturian.primary_audio' || registration.kind === 'arcturian.status_lookup') {
      if (!registration.turnId) return;
      const turn = arcturianTurnsRef.current.get(registration.turnId);
      if (!turn) return;
      if (!turn.primaryToolResponses) turn.primaryToolResponses = new Map();
      let toolState = turn.primaryToolResponses.get(responseId);
      if (!toolState) {
        toolState = {
          pendingCallIds: new Set(),
          responseDone: false,
          continuationStarted: false,
        };
        turn.primaryToolResponses.set(responseId, toolState);
      }
      toolState.pendingCallIds.add(callId);
      turn.readToolCallCount = (turn.readToolCallCount || 0) + 1;
      void handleArcturianReadTool(
        responseId,
        registration.turnId,
        name,
        rawArgs,
        callId,
        turn.readToolCallCount <= MAX_ARCTURIAN_READ_CALLS_PER_TURN,
      );
      return;
    }
    if (registration.kind !== 'plain') return;
    if (name === 'propose_to_agent') void handleProposal(rawArgs, callId);
    else if (name === 'relay_to_agent') void handleRelay(rawArgs, callId);
  }, [handleArcturianReadTool, handleArcturianResolver, handleProposal, handleRelay, sendFunctionOutput,
    startNextArcturianPrimary, reportRealtimeFailure]);

  const handleEvent = useCallback((msg: RealtimeEvent) => {
    switch (msg.type) {
      // Talkback: the model called propose_to_agent → route to Cloud's gate.
      // Der Call wird ZUERST an seine response_id gebunden (#898). Trifft er zu
      // einer abgebrochenen oder unbekannten Response ein, wird er verworfen —
      // auch bei vollständig gültigen Argumenten. Vorher lief er ungeprüft
      // durch: fällt der Operator dem Narrator ins Wort, während dieser einen
      // Vorschlag formuliert, konnte der Vorschlag trotzdem noch abgehen.
      case 'response.function_call_arguments.done': {
        const rid = typeof msg.response_id === 'string' ? msg.response_id : '';
        const callId = typeof msg.call_id === 'string' ? msg.call_id : '';
        if (callId && typeof msg.name === 'string' && typeof msg.arguments === 'string') {
          routeFunctionCall(rid, callId, msg.name, msg.arguments);
        }
        break;
      }
      // The operator's OWN spoken words (transcribed) — log them so the Log is a
      // full two-way trail for verification (Alex 2026-06-24: "ich sehe meine
      // Aussagen nicht").
      case 'conversation.item.input_audio_transcription.completed': {
        const t = (msg.transcript as string) || '';
        if (t.trim()) setTranscript((prev) => [...prev, { role: 'you', text: t, ts: Date.now() }]);
        break;
      }
      // A narration started — register the slot under its response_id and block
      // a parallel one.
      case 'response.created': {
        const rid = ((msg.response as Record<string, unknown>)?.id as string) || '';
        const meta = ((msg.response as Record<string, unknown>)?.metadata as Record<string, unknown>) || null;
        const v = wireRef.current.responseCreated(rid, meta);
        if (v.accepted) {
          if (v.registration.turnId) {
            const turn = arcturianTurnsRef.current.get(v.registration.turnId);
            if (turn && v.registration.kind === 'arcturian.resolver') turn.resolverResponseId = rid;
            if (turn && v.registration.kind === 'arcturian.primary_audio') turn.primaryResponseId = rid;
          }
          activeResponseIdRef.current = rid;
          responseActiveRef.current = true;
          armTerminalWatchdog();
        } else if (companionModeRef.current === 'arcturian') {
          if (rid && dcRef.current?.readyState === 'open') {
            dcRef.current.send(JSON.stringify({ type: 'response.cancel', response_id: rid }));
          }
          cleanupConnection();
          setError(`Arcturian-Response verworfen: ${v.reason}`);
          setState('error');
        }
        break;
      }
      // Top-level cancelled/incomplete ist laut Wire v1 NUR Alias-Fallback und
      // ausdrücklich KEIN Terminal. Früher wurde hier sofort wieder narriert —
      // ein danach eintreffendes verspätetes Event der abgebrochenen Response
      // traf dann auf den bereits gestarteten neuen Turn. Jetzt: nur sperren,
      // das terminale `response.done` gibt frei (Watchdog als Notbremse).
      case 'response.cancelled':
      case 'response.incomplete': {
        const rid = ((msg.response as Record<string, unknown>)?.id as string)
          || (typeof msg.response_id === 'string' ? msg.response_id : '')
          || activeResponseIdRef.current || '';
        wireRef.current.aliasTerminal(rid);
        if (rid) wireRef.current.markCancelling(rid);
        break;
      }
      // Spoken narration transcript (model → operator) — nur aus der Response,
      // die wir gerade führen. Ein verspätetes Transkript einer abgebrochenen
      // Response landete sonst im aktuellen Turn.
      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done': {
        const rid = typeof msg.response_id === 'string' ? msg.response_id : '';
        if (!wireRef.current.audioDelta(rid).accepted) break;
        const t = (msg.transcript as string) || '';
        if (t.trim()) setTranscript((prev) => [...prev, { role: 'voice', text: t, ts: Date.now() }]);
        break;
      }
      // Usage accounting — exactly one POST, no retry. + release the response
      // lock and narrate again if new feed arrived while it was speaking.
      case 'response.done': {
        const resp = (msg.response as Record<string, unknown>) || {};
        // Primäre und einzige Terminalquelle: response.done.response.id.
        // Freigegeben wird nur, wenn es DIE Response ist, die wir führen —
        // ein verspätetes done einer alten Response darf den laufenden Turn
        // nicht beenden.
        const doneId = (resp.id as string) || '';
        // OpenAI may deliver a function call only in response.done.output. Route
        // this fallback before marking the response terminal; the wire dedups it
        // against response.function_call_arguments.done via call_id.
        const output = Array.isArray(resp.output) ? resp.output as Array<Record<string, unknown>> : [];
        for (const item of output) {
          if (item.type === 'function_call'
              && typeof item.call_id === 'string'
              && typeof item.name === 'string'
              && typeof item.arguments === 'string') {
            routeFunctionCall(doneId, item.call_id, item.name, item.arguments);
          }
        }
        const wasCancelling = wireRef.current.get(doneId)?.cancelling === true;
        const term = wireRef.current.responseDone(doneId, (resp.metadata as Record<string, unknown>) || null);
        if (term.accepted && (!activeResponseIdRef.current || activeResponseIdRef.current === doneId)) {
          clearTerminalWatchdog();
          activeResponseIdRef.current = null;
          responseActiveRef.current = false;
          const registration = term.registration;
          const turn = registration.turnId
            ? arcturianTurnsRef.current.get(registration.turnId)
            : undefined;
          if (registration.kind === 'arcturian.resolver' && turn) {
            turn.resolverDone = true;
            if (!turn.resolverCallSeen) {
              setError('Arcturian-Resolver endete ohne Werkzeugaufruf');
              arcturianTurnsRef.current.delete(turn.turnId);
              if (pendingPttResponseRef.current) requestPttResponse();
            } else {
              startNextArcturianPrimary();
            }
          } else if (registration.kind === 'arcturian.report_intent' && turn) {
            // Auch ohne Absicht wird gemeldet: Ein Bericht ohne Absicht ist
            // unvollstaendig, ein fehlender Bericht nutzlos.
            const failure = turn.pendingReport;
            turn.pendingReport = undefined;
            turn.primaryRequested = false;
            if (failure) reportRealtimeFailure(failure);
            if (!wasCancelling) startArcturianPrimary(turn, doneId);
          } else if (registration.kind === 'arcturian.status_lookup' && turn) {
            const toolState = turn.primaryToolResponses?.get(doneId);
            turn.primaryRequested = false;
            if (toolState && !wasCancelling) {
              toolState.responseDone = true;
              continueArcturianPrimaryAfterTools(turn, doneId);
            } else if (!wasCancelling) {
              // Kein Werkzeugaufruf trotz `required`, oder der Nachschlag ist
              // fehlgeschlagen: Es wird TROTZDEM gesprochen. Ein erzwungener
              // Nachschlag, der nichts findet, darf nicht in Schweigen enden —
              // sonst ist Alex' „einen Moment" nur umgezogen.
              startArcturianPrimary(turn, doneId);
            }
          } else if (registration.kind === 'arcturian.primary_audio' && turn) {
            const toolState = turn.primaryToolResponses?.get(doneId);
            if (toolState && !wasCancelling) {
              // A primary response that called a read tool is not the final
              // spoken answer. Wait for every correlated output, then resend
              // the exact server template with this doneId as its source.
              toolState.responseDone = true;
              turn.primaryRequested = false;
              continueArcturianPrimaryAfterTools(turn, doneId);
            } else {
              const returnTurn = arcturianReturnTurnsRef.current.get(turn.turnId);
              if (returnTurn) {
                returnTurn.controller.markProjected(returnTurn.events);
                arcturianReturnTurnsRef.current.delete(turn.turnId);
              }
              arcturianTurnsRef.current.delete(turn.turnId);
              startNextArcturianPrimary();
              if (!responseActiveRef.current && pendingPttResponseRef.current) requestPttResponse();
              else if (!responseActiveRef.current) {
                // Own action returns outrank opt-in status chatter. If none is
                // pending, resume a focused-agent update that arrived while the
                // previous primary audio was speaking.
                if (arcturianReturnControllerRef.current?.pending().length) {
                  scheduleArcturianReturnNarration();
                } else if (dirtyRef.current) {
                  scheduleNarration();
                }
              }
            }
          } else if (registration.kind === 'plain') {
            if (pendingPttResponseRef.current) requestPttResponse();
            else if (dirtyRef.current) scheduleNarration();
            else scheduleArcturianReturnNarration();
          }
        }
        const usage = (resp.usage as Record<string, unknown>) || {};
        const inDet = (usage.input_token_details as Record<string, number>) || {};
        const outDet = (usage.output_token_details as Record<string, number>) || {};
        const rec: UsageRecord = {
          voice_session_id: voiceSessionId.current,
          usage_event_id: (resp.id as string) || `resp_${performance.now()}`,
          audio_input_tokens: inDet.audio_tokens,
          audio_output_tokens: outDet.audio_tokens,
          text_input_tokens: inDet.text_tokens,
          text_output_tokens: outDet.text_tokens,
        };
        const recTokens = (rec.audio_input_tokens || 0) + (rec.audio_output_tokens || 0)
          + (rec.text_input_tokens || 0) + (rec.text_output_tokens || 0);
        // In-flight is tracked as `pending` so allUsageAcked isn't falsely true
        // mid-POST (Codex metric-race fix): pending++ before, -- on settle.
        setUsage((u) => ({ ...u, pending: u.pending + 1 }));
        void reportUsage(rec, apiBaseRef.current || undefined).then((ack) => {
          // ACK only when the server confirms accepted|deduped (Codex' rule).
          if (ack.ok && (ack.accepted || ack.deduped)) {
            setUsage((u) => ({ ...u, pending: u.pending - 1, acked: u.acked + 1 }));
          } else {
            unsentOrUnknown.current.push(rec); // unsent_or_unknown, never re-sent
            setUsage((u) => ({ ...u, pending: u.pending - 1, unsent: u.unsent + 1, unsentTokens: u.unsentTokens + recTokens }));
          }
        });
        break;
      }
    }
  }, [scheduleNarration, scheduleArcturianReturnNarration, requestPttResponse, routeFunctionCall, startNextArcturianPrimary, continueArcturianPrimaryAfterTools, armTerminalWatchdog, clearTerminalWatchdog, cleanupConnection]);

  // Operator confirms / rejects a held talkback proposal (the confirm-chip).
  const confirmPendingProposal = useCallback(async (editedText?: string) => {
    const p = pendingProposal;
    if (!p) return;
    setPendingProposal(null);
    const result = await confirmVoiceProposal(
      p,
      editedText,
      voiceSessionId.current,
      focusEpochRef.current,
      voiceGate,
    );
    respondToModel(p.callId, result.confirmed, result.confirmed ? editedText : undefined);
    if (!result.confirmed) {
      setTranscript((prev) => [...prev, {
        role: 'voice',
        text: `✗ nicht gesendet (${p.target}): ${result.reason || 'Bestätigung fehlgeschlagen'}`,
        ts: Date.now(),
      }]);
      return;
    }
    setTranscript((prev) => [...prev, { role: 'voice', text: `✓ an ${p.target} gesendet`, ts: Date.now() }]);
  }, [pendingProposal, respondToModel]);

  const rejectPendingProposal = useCallback(() => {
    const p = pendingProposal;
    if (!p) return;
    setPendingProposal(null);
    respondToModel(p.callId, false);
    setTranscript((prev) => [...prev, { role: 'voice', text: `✗ nicht gesendet (${p.target})`, ts: Date.now() }]);
  }, [pendingProposal, respondToModel]);

  const refreshAudioInputs = useCallback(async (): Promise<AudioInputOption[]> => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return [];
    try {
      const options = normalizeAudioInputs(await navigator.mediaDevices.enumerateDevices());
      setAudioInputs(options);
      return options;
    } catch {
      return [];
    }
  }, []);

  const rememberAudioInput = useCallback((deviceId: string) => {
    selectedAudioInputRef.current = deviceId;
    setSelectedAudioInputId(deviceId);
    try {
      if (deviceId) localStorage.setItem(AUDIO_INPUT_KEY, deviceId);
      else localStorage.removeItem(AUDIO_INPUT_KEY);
    } catch { /* private mode */ }
  }, []);

  const updateActiveAudioInput = useCallback(async (stream: MediaStream) => {
    const options = await refreshAudioInputs();
    setActiveAudioInputLabel(activeAudioInput(stream, options).label);
  }, [refreshAudioInputs]);

  const selectAudioInput = useCallback(async (deviceId: string) => {
    if (audioInputSwitchingRef.current) return;
    const sender = audioSenderRef.current;
    if (!micRef.current || !sender) {
      rememberAudioInput(deviceId);
      setAudioInputError('');
      return;
    }
    audioInputSwitchingRef.current = true;
    setAudioInputSwitching(true);
    setAudioInputError('');
    try {
      const next = await navigator.mediaDevices.getUserMedia({ audio: audioConstraint(deviceId) });
      await replaceAudioInput(micRef.current, next, sender, inputMutedRef.current);
      micRef.current = next;
      setMicStream(next);
      rememberAudioInput(deviceId);
      await updateActiveAudioInput(next);
    } catch (error) {
      setAudioInputError(`Eingang nicht gewechselt: ${friendlyAudioInputError(error)}`);
    } finally {
      audioInputSwitchingRef.current = false;
      setAudioInputSwitching(false);
    }
  }, [rememberAudioInput, updateActiveAudioInput]);

  const requestAudioInputAccess = useCallback(async () => {
    if (audioInputSwitchingRef.current) return;
    audioInputSwitchingRef.current = true;
    setAudioInputSwitching(true);
    setAudioInputError('');
    let probe: MediaStream | null = null;
    try {
      probe = await navigator.mediaDevices.getUserMedia({ audio: true });
      await refreshAudioInputs();
    } catch (error) {
      setAudioInputError(`Mikrofone nicht verfügbar: ${friendlyAudioInputError(error)}`);
    } finally {
      probe?.getTracks().forEach((track) => track.stop());
      audioInputSwitchingRef.current = false;
      setAudioInputSwitching(false);
    }
  }, [refreshAudioInputs]);

  useEffect(() => {
    const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!mediaDevices) return;
    const onDeviceChange = () => {
      void refreshAudioInputs().then((options) => {
        const selected = selectedAudioInputRef.current;
        if (micRef.current && shouldReacquireAudioInput(selected, options)) {
          void selectAudioInput('');
        } else if (selected && !options.some((option) => option.deviceId === selected)) {
          rememberAudioInput('');
        }
      });
    };
    void refreshAudioInputs();
    mediaDevices.addEventListener?.('devicechange', onDeviceChange);
    return () => mediaDevices.removeEventListener?.('devicechange', onDeviceChange);
  }, [refreshAudioInputs, rememberAudioInput, selectAudioInput]);

  const start = useCallback(async (initialTarget: string, arcturianConversationId?: string) => {
    if (!initialTarget) return;
    targetRef.current = initialTarget; setTarget(initialTarget);
    setState('connecting'); setError(''); setTranscript([]); setInputMutedState(true); inputMutedRef.current = true; setPendingProposal(null); setAgentTransparent(false);
    setAudioInputError(''); setActiveAudioInputLabel('');
    setUsage({ acked: 0, unsent: 0, unsentTokens: 0, pending: 0 });
    unsentOrUnknown.current = [];
    dirtyRef.current = false; responseActiveRef.current = false; firstUnspokenRef.current = null;
    arcturianInformingRef.current = false;
    setArcturianInformingState(false);
    focusEpochRef.current = 0; segmentIdRef.current = 0; ambientTargetRef.current = null;
    clearTriggerTimers();
    // ONE voice_session_id for the whole companion run — focus shifts are events
    // inside it, not new sessions (Alex' continuous-flow directive).
    const now = Math.round(performance.now());
    voiceSessionId.current = `vs_${initialTarget}_${now}`;
    companionRunId.current = `cr_${initialTarget}_${now}`;
    suppressedContextItemsRef.current = 0;
    setMeta({
      id: voiceSessionId.current,
      startedAt: 0,
      endedAt: null,
      suppressedContextItems: 0,
    });
    try {
      // Realtime mode from Settings (#1235): narrator (3rd-person read-only) /
      // talkback (relay commands w/ confirm-chip) / transparent (1st-person agent
      // skin + direct relay). Falls back to the legacy talkback toggle when unset.
      const storedMode = (typeof localStorage !== 'undefined' && localStorage.getItem('cloud-voice-mode'))
        || (talkbackRef.current ? 'talkback' : 'narrator');
      const voiceMode = storedMode;
      const companionMode: CompanionMode = voiceMode === 'arcturian'
        ? 'arcturian'
        : voiceMode === 'transparent'
          ? 'agent-transparent'
          : voiceMode === 'talkback'
            ? 'talkback-enabled'
            : 'agentos-narrator';
      setAgentTransparent(companionMode === 'agent-transparent');
      companionModeRef.current = companionMode;
      let canonicalConversationId = arcturianConversationId?.trim() || undefined;
      let returnPointer: ArcturianActionPointer | null = null;
      if (companionMode === 'arcturian') {
        // Action replies use the already-live owner-scoped conversation-event
        // projection, not Phase-2 context subscriptions. Discovering the latest
        // action is best-effort and cannot block the voice transport.
        try {
          const actions = await arcturianActionApi.list();
          returnPointer = latestArcturianActionPointer(actions, canonicalConversationId);
        } catch {
          // A new action executed in this session binds the return lane from its
          // validated receipt, so an unavailable discovery read is degradable.
        }
      }
      // Narration verbosity from Settings (default 'balanced' — Alex likes the
      // richness of what the agent SAYS; the background/process noise is handled
      // separately via the `cloud-voice-bg` feed toggle). 'flowing' (#1235) lets the
      // model trim flow-breaking technical asides. Frozen at mint.
      const detailLevel = (typeof localStorage !== 'undefined' && localStorage.getItem('cloud-voice-detail')) || 'balanced';
      const tok = await mintToken({
        sessionId: initialTarget,
        mode: companionMode,
        apiBase: apiBaseRef.current || undefined,
        detailLevel,
        companionRunId: companionRunId.current,
        voiceSessionId: voiceSessionId.current,
        pushToTalk: true,
        ...(companionMode === 'arcturian'
          ? { arcturianResolver: ARCTURIAN_RESOLVER_V3, readTools: true }
          : {}),
        ...(companionMode === 'arcturian' && canonicalConversationId
          ? { conversationId: canonicalConversationId }
          : {}),
      });
      if (tok.error || !tok.client_secret) {
        // Die Lücke, die der Abend vom 11.08. gezeigt hat: Ein gescheiterter
        // Mint erzeugte KEINEN Eintrag. Für Alex sah eine seit Stunden tote
        // Sprachschicht aus wie „startet nicht", während die Ursache — ein
        // `UnboundLocalError` in einem fremden Dienst — in einem Protokoll auf
        // einem anderen Rechner lag. Ausgerechnet der Fehler, der JEDE Sitzung
        // verhindert, meldete sich als Einziger nicht selbst.
        //
        // Der Grund gehört ins Protokoll, das Gesagte nicht: `token 500` und
        // `realtime_not_enabled` sind Dienstantworten, kein Gesprächsinhalt.
        reportRealtimeFailure({
          code: 'realtime_mint_failed',
          mode: companionMode,
          contract: companionMode === 'arcturian' ? ARCTURIAN_RESOLVER_V3 : undefined,
          rule: tok.error || 'Antwort ohne client_secret',
          voiceSessionId: voiceSessionId.current || undefined,
          build: __BUILD_ID__,
          endpoint: apiBaseRef.current || undefined,
        });
        // Das Fehler-Detail an den Fehler heften, damit die Anzeige unten die
        // Budget-Auskunft (Fenster, Verbrauch, Wiederfreigabe) zeigen kann und
        // nicht nur das Stichwort. Ohne das stand fünf Tage lang „Tageslimit
        // erreicht — morgen wieder verfügbar" an einem MONATS-Fenster.
        throw Object.assign(new Error(tok.error || 'Token-Mint fehlgeschlagen'), {
          voiceDetail: tok.error_detail,
        });
      }
      // Fail closed on the server's ACTUAL mint result. `push_to_talk:true` is
      // only a request; without both echoed truths a tool-bearing mode could
      // still run server-VAD while this client also commits manually, producing
      // duplicate or uncorrelated responses.
      const pttError = pttContractError(tok);
      if (pttError) throw new Error(pttError);
      // Mint succeeded → a server-side reservation now exists under our session_id.
      // Remember the key so cleanup releases it even if the guard below throws.
      // Der Server reserviert den Platz unter `voice_session_id`, NICHT unter
      // `session_id` (realtime_routes.py:3397: „voice_session_id FIRST — it is
      // the only field whose meaning is 'the id this session will heartbeat and
      // report usage with'"). Hier stand der Agentenname, also ging jeder
      // Heartbeat auf einen Schlüssel, den der Server nicht kennt: `alive:false`
      // — und Zeile ~647 macht daraus ein stop(). Jede Web-Sitzung starb damit
      // nach spätestens 30 Sekunden.
      //
      // Derselbe Fehler ist auf iOS aufgetreten (18 von 18 Beats ins Leere,
      // AppDevV2 2026-08-05); dort fiel er nicht auf, weil der Client den Socket
      // als maßgeblich behandelt statt den Heartbeat. Gefunden hat es
      // CloudV2-Codex im Audit #964.
      reservationKey.current = voiceSessionId.current;
      // Contract guard, fail-closed BEFORE getUserMedia/SDP (AiApi): read-only
      // modes MUST be agentos-narrator + tools===[]; talkback MUST be exactly
      // talkback-enabled + tools===["propose_to_agent"]. Anything else → abort.
      let prefaceInjector: ArcturianPrefaceInjector | null = null;
      if (companionMode === 'arcturian') {
        // Arcturian prüft NICHT gegen eine hier hinterlegte Erwartung, sondern
        // gegen den Vertrag, den der Mint selbst mitliefert (response_kind_field,
        // server/adapter-Kinds, Korrelationsschlüssel, Primary mit tools=[] +
        // tool_choice=none). AiApi schickt ihn ausdrücklich mit, damit Clients
        // dagegen VALIDIEREN statt ihn nachzubauen — daran ist die iOS-App
        // gescheitert (invalidTokenContract, Issue #886).
        const v = verifyArcturianMint(tok as unknown as MintContract);
        // Die teuerste Meldestelle, und deshalb die wichtigste: Hier bricht die
        // Sitzung VOR Mikrofon und Verbindung ab, es gibt also weder ein
        // Gespräch noch einen Agenten, der davon erzählen könnte. Am 11.08.
        // stand Arcturian so eine Stunde still, weil AiApi die Adapter-Menge
        // erweitert hat und diese Prüfung sie auf Gleichheit verglich — sichtbar
        // war nur „geht nicht". Mit Eintrag hätte die Ursache dagestanden.
        if (!v.ok || !tok.resolver_followup || !tok.primary_audio_response || !tok.arcturian_resolver) {
          reportRealtimeFailure({
            code: v.ok ? 'arcturian_mint_incomplete' : 'arcturian_mint_contract_invalid',
            mode: 'arcturian',
            contract: typeof tok.arcturian_resolver === 'string' ? tok.arcturian_resolver : undefined,
            rule: v.ok ? 'resolver_followup / primary_audio_response / arcturian_resolver fehlt' : v.reason,
            build: __BUILD_ID__,
            endpoint: apiBaseRef.current || undefined,
          });
        }
        if (!v.ok) {
          throw new Error(`Arcturian-Token-Contract ungültig: ${v.reason} — Abbruch vor Mic/Verbindung`);
        }
        if (!tok.resolver_followup || !tok.primary_audio_response || !tok.arcturian_resolver) {
          throw new Error('Arcturian-Token-Contract unvollständig — Abbruch vor Mic/Verbindung');
        }
        arcturianContractRef.current = {
          resolverVersion: tok.arcturian_resolver,
          resolver: tok.resolver_followup,
          primary: tok.primary_audio_response,
          affect: tok.affect_followup,
          statusLookup: tok.status_lookup_response,
          reportIntent: tok.report_intent_response,
          conversationId: canonicalConversationId || tok.conversation_id || companionRunId.current,
        };
        // Validate the temporary Phase-1 replay contract before media access.
        // Injection itself happens synchronously in onopen, before `live` lets
        // the first PTT release commit a user turn.
        const preparedPreface = prepareArcturianPreface(tok, Boolean(canonicalConversationId));
        prefaceInjector = new ArcturianPrefaceInjector(preparedPreface);
        if (returnPointer) {
          // A return subscription and a conversation preface are separate
          // capabilities. The latest action may be watched for a delayed result,
          // but only an explicit caller-provided conversation may seed Realtime
          // history. Otherwise an old assistant failure becomes the new session's
          // apparent truth (#977 live smoke, cr_AppDevV2_208453).
          const returnBaseline = returnPointer.conversationId === canonicalConversationId
            ? preparedPreface.throughRevision
            : null;
          bindArcturianReturn(
            returnPointer,
            new PrefaceReturnBaselineAdapter(returnBaseline),
          );
        }
        wireRef.current = new ArcturianWire({ requireKind: true, requireExpected: true });
      } else if (companionMode === 'talkback-enabled') {
        const okTools = Array.isArray(tok.tools) && tok.tools.length === 1 && tok.tools[0] === 'propose_to_agent';
        if (tok.companion_mode !== 'talkback-enabled' || !okTools) {
          throw new Error('Talkback-Token-Contract ungültig (erwarte talkback-enabled + tools=[propose_to_agent]) — Abbruch');
        }
      } else if (companionMode === 'agent-transparent') {
        const okTools = Array.isArray(tok.tools) && tok.tools.length === 1 && tok.tools[0] === 'relay_to_agent';
        if (tok.companion_mode !== 'agent-transparent' || !okTools) {
          throw new Error('Transparent-Token-Contract ungültig (erwarte agent-transparent + tools=[relay_to_agent]) — Abbruch');
        }
      } else if (tok.companion_mode !== 'agentos-narrator' || !Array.isArray(tok.tools) || tok.tools.length !== 0) {
        throw new Error('Read-only-Token-Contract ungültig (erwarte agentos-narrator + tools=[]) — Abbruch vor Mic/Verbindung');
      }
      if (companionMode !== 'arcturian') {
        arcturianContractRef.current = null;
        wireRef.current = new ArcturianWire({ requireKind: false });
      }

      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.ontrack = (e) => {
        if (audioRef.current) {
          audioRef.current.srcObject = e.streams[0];
          audioRef.current.play().catch(() => { /* needs a gesture; toggle tap provides it */ });
        }
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          cleanupConnection(); setError('Verbindung verloren'); setState('error');
        }
      };

      // Preserve browser-native processing (explicit AEC constraints filtered
      // Alex's own voice away on iOS), but choose the requested physical input.
      // A stale persisted device (e.g. disconnected AirPods) falls back once to
      // the browser default so the whole Realtime session still starts.
      const acquired = await acquireAudioInput(
        selectedAudioInputRef.current,
        (constraints) => navigator.mediaDevices.getUserMedia(constraints),
      );
      const mic = acquired.stream;
      if (acquired.fellBackToDefault) {
        rememberAudioInput('');
        setAudioInputError('Gewähltes Mikrofon nicht verfügbar — Systemstandard wird verwendet.');
      }
      micRef.current = mic;
      setMicStream(mic); // expose for the VU meter
      await updateActiveAudioInput(mic);
      mic.getTracks().forEach((t) => {
        const sender = pc.addTrack(t, mic);
        if (t.kind === 'audio') {
          // Keep the negotiated sender but detach its source until the operator
          // holds PTT. This prevents silence/hallucinations and accidental audio
          // upload while preserving the live Realtime connection.
          t.enabled = false;
          audioSenderRef.current = sender;
          void sender.replaceTrack(null);
        }
      });

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;
      openedRef.current = false;
      teardownRef.current = false;
      dc.onmessage = (ev) => { try { handleEvent(JSON.parse(ev.data) as RealtimeEvent); } catch { /* non-JSON */ } };
      // Abort if the channel never opens — else we'd hang in 'connecting' with a
      // live mic and no auto-stop (Codex 2026-06-24).
      connectTimerRef.current = setTimeout(() => {
        connectTimerRef.current = null;
        if (!openedRef.current) { cleanupConnection(); setError('DataChannel konnte nicht geöffnet werden'); setState('error'); }
      }, CONNECT_TIMEOUT_MS);
      // Go 'live' ONLY when the data channel is actually OPEN — otherwise the
      // first feed (the priming!) fires while readyState==='connecting' and is
      // silently dropped, so the narrator starts with no context and invents
      // (Alex 2026-06-24: empty Log + hallucination). onopen guarantees sends land.
      dc.onopen = () => {
        openedRef.current = true;
        if (connectTimerRef.current) { clearTimeout(connectTimerRef.current); connectTimerRef.current = null; }
        try {
          prefaceInjector?.inject(dc);
        } catch {
          cleanupConnection();
          setError('Arcturian-Verlauf konnte nicht sicher übernommen werden');
          setState('error');
          return;
        }
        // DER FOKUS BEIM START — Alex, 2026-08-12: „Ich bin dann in einen Agenten
        // rein gegangen und habe ihn gefragt, ob er den aktiven Agenten sehen
        // kann. Da hat er sich nicht ausgekannt."
        //
        // Der Fokus-WECHSEL wurde bisher gemeldet (`focusShift`), der
        // Anfangszustand nie: Wer einen Chat öffnet und DANN spricht, löst
        // keinen Wechsel aus — und der Mint trägt den Namen nur als Kennung,
        // nicht als Aussage. Damit wusste Arcturian in genau dem häufigsten
        // Fall nicht, wo er ist.
        //
        // `speak=false`: Das ist ein Stand, kein Ereignis. Er soll es wissen,
        // nicht ansagen — eine gesprochene Begrüßung bei jedem Sitzungsstart
        // wäre genau die Geschwätzigkeit, die wir sonst überall abstellen.
        if (companionMode === 'arcturian' && targetRef.current) {
          pushContextRef.current?.(
            'focus_boundary',
            targetRef.current,
            `[Fokus] Der Operator hat gerade den Agenten „${targetRef.current}" offen. `
            + `Spricht er von „dem aktiven Agenten", „dem hier", „dem offenen Chat" oder `
            + `sagt er gar keinen Namen, ist ${targetRef.current} gemeint. `
            + 'Kein Auftrag — nur der Stand.',
            false,
          );
        }
        if (autoStopTimer.current) clearTimeout(autoStopTimer.current);
        autoStopTimer.current = setTimeout(stop, MAX_SESSION_MS);
        // Liveness heartbeat every 30s so the server's short reap frees a phantom
        // slot fast (AiApi PR #71). alive===false → the slot is gone (reaped /
        // released elsewhere) → stop locally. Transient (null) → keep going.
        if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
        heartbeatTimer.current = setInterval(() => {
          const key = reservationKey.current;
          if (!key) return;
          void heartbeat(key, apiBaseRef.current || undefined).then((alive) => {
            if (alive === false) stop();
          });
        }, 30000);
        setMeta((m) => ({ ...m, startedAt: Date.now() }));
        // Input transcription and turn detection are frozen into the server-
        // minted token. Do not override them from the browser: the former flat
        // session field was removed from OpenAI's current Realtime wire (#967).
        setState('live');
        scheduleArcturianReturnNarration();
      };
      // Ignore close/error WE caused (teardownRef). Otherwise: before open =
      // connection failure; after open = unexpected remote close → clean stop.
      dc.onerror = () => {
        if (teardownRef.current) return;
        if (!openedRef.current) { cleanupConnection(); setError('DataChannel-Fehler beim Verbinden'); setState('error'); }
      };
      dc.onclose = () => {
        if (teardownRef.current) return;
        if (!openedRef.current) { cleanupConnection(); setError('Verbindung vor Öffnen geschlossen'); setState('error'); }
        else { stop(); }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const answerSdp = await openaiSdpExchange(tok.model, tok.client_secret, offer.sdp || '');
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      // state stays 'connecting' until dc.onopen flips it to 'live'.
    } catch (e) {
      cleanupConnection();
      setError(e instanceof Error
        ? friendlyVoiceError(friendlyAudioInputError(e), (e as { voiceDetail?: Record<string, unknown> }).voiceDetail)
        : 'Verbindung fehlgeschlagen');
      setState('error');
    }
  }, [
    handleEvent,
    cleanupConnection,
    clearTriggerTimers,
    rememberAudioInput,
    updateActiveAudioInput,
    bindArcturianReturn,
    scheduleArcturianReturnNarration,
    reportRealtimeFailure,
  ]);

  // Core: inject ONE structured context item, serialized as AiApi's canonical
  // v1 header (PR #63), the model already parses this bracket/`·` grammar:
  //   [ctx · source_agent=<Name> · context_kind=<kind> · focus_epoch=<n> · context_segment_id=<n>]
  //   <payload after a newline>
  // Codex' tight-grammar rules: source_agent is the only free value → strip
  // anything that could break the bracket/separator framing; context_kind is an
  // enum; epoch/segment are integers; payload strictly after the newline.
  // `source_agent` is ALWAYS explicit (never defaulted to the current target).
  const pushContext = useCallback((kind: ContextKind, agent: string, payload: string, speak = true, pairId?: string) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') return;
    if (!shouldInjectVoiceContext(companionModeRef.current, kind, arcturianInformingRef.current)) {
      suppressedContextItemsRef.current += 1;
      return;
    }
    // Redaction boundary: never send raw secrets/keys to OpenAI.
    const safe = sanitizeForFeed(payload);
    if (!safe.trim()) return;
    // Safety net (Codex 2026-06-24): a feed for an agent that is NOT the current
    // focus must never be emitted as a primary-focus kind — downgrade it to
    // background_work so it is only ever referenced as background, never mixed
    // into the current focus agent's narration.
    let k = kind;
    if (agent !== targetRef.current && (k === 'agent_message' || k === 'operator_request')) {
      k = 'background_work';
    }
    // Ambient-Ende deliberately carries an empty source_agent per AiApi's
    // contract; every other context keeps the historical AgentOS fallback.
    const cleanedAgent = agent.replace(/[[\]·\n]/g, ' ').trim();
    const who = kind === 'ambient_boundary' ? cleanedAgent : (cleanedAgent || 'AgentOS');
    const epoch = focusEpochRef.current;
    const seg = segmentIdRef.current;
    const header = `[ctx · source_agent=${who} · context_kind=${k} · focus_epoch=${epoch} · context_segment_id=${seg}${pairId ? ` · pair_id=${pairId}` : ''}]`;
    setTranscript((prev) => [...prev, { role: 'fed', text: safe, kind: k, agent: who || undefined, epoch, seg, ts: Date.now() }]);
    dc.send(JSON.stringify({
      type: 'conversation.item.create',
      item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: `${header}\n${safe}` }] },
    }));
    // Informing never uses a naked response.create. The shared scheduler calls
    // the correlated primary-audio template for Arcturian; all other modes keep
    // their existing plain narration. Only true focused-agent deltas trigger an
    // unsolicited update — operator/history/navigation context may inform that
    // sentence but never starts one by itself.
    const shouldNarrate = companionModeRef.current !== 'arcturian'
      || (arcturianInformingRef.current && k === 'agent_message');
    if (speak && shouldNarrate) {
      dirtyRef.current = true;
      if (firstUnspokenRef.current == null) firstUnspokenRef.current = performance.now();
      scheduleNarration();
    }
  }, [scheduleNarration]);
  pushContextRef.current = pushContext;

  // Typed feed surface (Codex fix 1): every call carries its true context_kind +
  // an EXPLICIT source_agent, so the operator's own request is never narrated as
  // an agent message and a late feed is never mis-attributed to the new focus.
  const feedAgentMessage = useCallback((text: string, agent: string) => {
    // Transparent mode step 6 (#1235, envelope confirmed by AiApi PR #78/#82): if this
    // turn is the response to a pending relay, thread it back as agent_voice_response
    // with the relay's pair_id, so the model voices it as its OWN 1st-person answer AND
    // knows the wait is over — ending the pre-answer guessing. Else: normal message.
    const pr = pendingRelay.current;
    if (pr && pr.target === agent) {
      pendingRelay.current = null;
      pushContext('agent_voice_response', agent, text, true, pr.pairId);
    } else {
      pushContext('agent_message', agent, text);
    }
  }, [pushContext]);
  const feedOperatorRequest = useCallback((text: string, agent: string) => pushContext('operator_request', agent, text), [pushContext]);
  const feedHistory = useCallback((text: string, agent: string) => pushContext('summary', agent, text), [pushContext]);

  // Continuous-flow focus shift (Alex' directive): the operator moved to another
  // agent. NO teardown, NO re-mint — bump focus_epoch + segment and inject a hard
  // focus_boundary so the AgentOS narrator re-anchors on Y in the SAME
  // conversation, keeping all prior context. Late old-epoch events stay background.
  const focusShift = useCallback((newTarget: string) => {
    if (!newTarget || newTarget === targetRef.current) return;
    targetRef.current = newTarget; setTarget(newTarget);
    focusEpochRef.current += 1;
    segmentIdRef.current += 1;
    // Barge-in (Alex 2026-06-24): drop the OLD focus' unspoken narration backlog
    // so the narrator doesn't drone through minutes of old-agent content before
    // reaching the new agent. We deliberately DO NOT cancel the in-flight
    // response — the line currently being spoken finishes naturally — we only
    // clear the pending trigger + dirty flag so the NEXT narration is the new
    // focus, not the accumulated backlog. (Alex: "aktuelle Zeile ausreden, dann
    // weiter mit dem neuen Agenten" — er musste 3 min auf den Backlog warten.)
    clearTriggerTimers();
    dirtyRef.current = false;
    firstUnspokenRef.current = null;
    pushContext(
      'focus_boundary',
      newTarget,
      `[Fokuswechsel] Der Operator betrachtet jetzt Agent „${newTarget}". Beziehe dich ab sofort auf ${newTarget} und überspringe nicht-erzählten Rückstand der vorherigen Agenten — die sind nur noch Hintergrund. Kein Neustart — du erinnerst dich an den bisherigen Verlauf.`,
    );
  }, [pushContext, clearTriggerTimers]);

  // Ambient-Roam is only a temporary narration source. It advances the context
  // segment and emits an ambient_boundary, but deliberately leaves targetRef and
  // focusEpoch untouched so talkback continues to address the operator's focus.
  const ambientShift = useCallback((newTarget: string | null) => {
    const next = transitionAmbientContext({
      operatorFocus: targetRef.current,
      focusEpoch: focusEpochRef.current,
      ambientSource: ambientTargetRef.current,
      segmentId: segmentIdRef.current,
    }, newTarget);
    if (!next.changed) return;
    ambientTargetRef.current = next.ambientSource;
    segmentIdRef.current = next.segmentId;
    pushContext(
      'ambient_boundary',
      next.boundarySource || '',
      next.boundarySource
        ? `[Ambient-Blick] Die Narrationsquelle wechselt vorübergehend zu „${next.boundarySource}". Der Operator-Fokus bleibt unverändert.`
        : '[Ambient-Ende] Die vorübergehende Narrationsquelle endet. Der Operator-Fokus bleibt unverändert.',
      false,
    );
  }, [pushContext]);

  const feedAmbientMessage = useCallback((text: string, agent: string) => {
    if (!agent || ambientTargetRef.current !== agent) return;
    pushContext('ambient_message', agent, text);
  }, [pushContext]);

  const feedAmbientHistory = useCallback((text: string, agent: string) => {
    if (!agent || ambientTargetRef.current !== agent) return;
    pushContext('ambient_message', agent, text);
  }, [pushContext]);

  // AgentOS-navigation awareness (Alex 2026-06-24): app-level events the narrator
  // should KNOW about — operator opened the dashboard, returned to the list, etc.
  // Pure context by default (speak:false) so navigation doesn't make it chatty.
  // DORMANT until AiApi's prompt has the app_event enum + it's wired to central
  // navigation (Codex: don't report as delivered yet).
  const appEvent = useCallback((description: string, speak = false) => {
    pushContext('app_event', targetRef.current || 'AgentOS', `[AgentOS-Ereignis] ${description}`, speak);
  }, [pushContext]);

  // Mic mute (Alex 2026-06-24): truly silence the input. `enabled=false` alone
  // still streams digital silence → Whisper hallucinates phantom phrases the model
  // then acts on. So we ALSO replaceTrack(null) on the sender → OpenAI receives no
  // audio at all → no phantom transcription/response. Unmute re-attaches the live
  // mic track (no getUserMedia, connection stays). enabled toggled too as backup.
  const setInputMuted = useCallback((muted: boolean) => {
    inputMutedRef.current = muted;
    micRef.current?.getAudioTracks().forEach((t) => { t.enabled = !muted; });
    const sender = audioSenderRef.current;
    if (sender) {
      if (muted) { void sender.replaceTrack(null); }
      else { const t = micRef.current?.getAudioTracks()[0]; if (t) void sender.replaceTrack(t); }
    }
    setInputMutedState(muted);
  }, []);

  // True PTT for every Web mode. The mint disables provider VAD for this
  // session, so release must close the audio buffer and explicitly request one
  // response. Duplicate begin/end events are expected from pointer-capture
  // cleanup and remain harmless.
  const pushToTalk = useCallback((active: boolean) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') {
      if (!active) setInputMuted(true);
      return;
    }
    if (active) {
      if (pttHeldRef.current) return;
      pttHeldRef.current = true;
      pttStartedAtRef.current = performance.now();
      // Human speech has priority over Arcturian's current spoken answer. Do
      // not cancel the resolver: it may still carry a valid action call and
      // #886 requires that call to reach the executor. Primary audio is
      // tool-less by contract, so it is safe to cancel through the wire and
      // wait for its terminal response.done before starting the queued turn.
      const activeResponseId = activeResponseIdRef.current;
      const activeRegistration = activeResponseId
        ? wireRef.current.get(activeResponseId)
        : undefined;
      if (activeResponseId
          && shouldInterruptVoiceResponse(companionModeRef.current, activeRegistration?.kind)) {
        // Local silence follows the operator gesture, not the remote protocol
        // race. Even if the wire slot became terminal between get() and
        // markCancelling(), Alex must not keep hearing it while speaking.
        if (audioRef.current) {
          audioRef.current.muted = true;
          audioRef.current.pause();
        }
        const cancelling = wireRef.current.markCancelling(activeResponseId);
        if (cancelling.accepted) {
          dc.send(JSON.stringify({ type: 'response.cancel', response_id: activeResponseId }));
        }
      }
      setInputMuted(false);
      return;
    }
    if (!pttHeldRef.current) return;
    pttHeldRef.current = false;
    setInputMuted(true);
    const heldForMs = performance.now() - pttStartedAtRef.current;
    pttStartedAtRef.current = 0;
    const events = pttReleaseEvents(heldForMs);
    for (const event of events) {
      if (event.type === 'response.create') requestPttResponse();
      else dc.send(JSON.stringify(event));
    }
    scheduleArcturianReturnNarration();
    if (dirtyRef.current) scheduleNarration();
  }, [requestPttResponse, scheduleArcturianReturnNarration, scheduleNarration, setInputMuted]);

  // Semantic boundary (turn/tool end, error, needs-input) — ensure the pending
  // feed gets narrated at the next allowed point (coalesced + never parallel).
  const narrateBoundary = useCallback(() => { scheduleNarration(); }, [scheduleNarration]);

  // Tear down only on real unmount (the provider lives for the whole app, so this
  // fires on app teardown — NOT on agent navigation, which is the whole point).
  useEffect(() => () => { cleanupConnection(); }, [cleanupConnection]);

  // `all_usage_acked` (Codex fix 5 + race-fix): final only when nothing is
  // in-flight AND nothing unsent.
  const allUsageAcked = usage.pending === 0 && usage.unsent === 0;
  return {
    state, error, transcript, usage, allUsageAcked, meta, target, audioRef,
    start, stop, focusShift, ambientShift, appEvent, inputMuted, narrateBoundary,
    feedAgentMessage, feedOperatorRequest, feedHistory,
    feedAmbientMessage, feedAmbientHistory,
    pendingProposal, confirmPendingProposal, rejectPendingProposal,
    agentTransparent, arcturianInforming, pushToTalk, micStream,
    audioInputs, selectedAudioInputId, activeAudioInputLabel,
    audioInputSwitching, audioInputError, selectAudioInput, requestAudioInputAccess,
  };
}
